import { useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_TARGET, isNativeApp, nativePlugin, isExternalUrl, openExternal, appPathFromUrl } from "@/lib/nativeApp";

/**
 * What the site does differently inside the Toukky apps, in one place and
 * only there (it renders nothing and does nothing in a browser):
 *   - hides the native splash once the page is up, sets a dark status bar;
 *   - Android's back button first closes an open sheet or dialog, never
 *     leaves a payment being processed, then walks back through the pages,
 *     then goes to the app's first screen, and leaves the app only from there;
 *   - the page may run under the phone's bars (viewport-fit=cover), the
 *     apps' layer (index.css) keeping its content clear of them;
 *   - a deep link (toukky://…, toukkypartner://…, https://www.toukky.com/…)
 *     opens its page inside the app;
 *   - a link to another site opens in the phone's browser, never inside the
 *     app's web view;
 *   - once someone is signed in, the phone is registered for push
 *     notifications (after the system asks), and tapping one opens its page.
 */
export default function NativeBridge() {
  const nav = useNavigate();
  // The screen shown now, read by the back button (the router's, which the
  // web view's address follows).
  const here = useRef("/");
  here.current = useLocation().pathname;
  const { user } = useAuth();
  const userId = user?.user_id || null;
  const launchRead = useRef(null), launchConsumed = useRef(false);

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    document.documentElement.classList.add("is-native-app");
    // The website's viewport stays as it is; only the apps run under the bars.
    document.querySelector('meta[name="viewport"]')?.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
    const App = nativePlugin("App");
    const handles = [];
    let live = true;
    const keep = (pending) => { Promise.resolve(pending).then((h) => {
      if (!h) return;
      if (live) handles.push(h); else h.remove?.();
    }).catch(() => {}); };
    const openLink = (url) => {
      if (!live) return;
      const path = appPathFromUrl(url);
      if (path && /^\/groups\/[^/]+\/join(?:\?|$)/.test(path)) {
        // Consume the fragment privately BEFORE navigation. Missing bootstrap,
        // rejected host or partner build must not use the generic fallback.
        if (APP_TARGET === "partner") return;
        const clean = window.toukkyGroupInvitation?.receive(url);
        if (clean) nav(clean);
      } else if (path) nav(path);
    };

    Promise.resolve(nativePlugin("StatusBar")?.setStyle?.({ style: "LIGHT" })).catch(() => {});
    Promise.resolve(nativePlugin("SplashScreen")?.hide?.()).catch(() => {});

    if (App?.addListener) {
      keep(App.addListener("backButton", ({ canGoBack }) => {
        // 1. An open sheet or dialog closes first (Radix closes on Escape).
        if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) {
          document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
          return;
        }
        // 2. A payment being processed is never left by the back button.
        if (document.querySelector('[data-testid="payment-busy"], [data-testid="pay-now"][aria-busy="true"]')) return;
        // 3. Back through the pages.
        if (canGoBack) { window.history.back(); return; }
        // 4. The app's first screen, then 5. leave.
        const home = APP_TARGET === "partner" ? "/partner/dashboard" : "/";
        if (here.current !== home) nav(home, { replace: true });
        else Promise.resolve(App.exitApp?.()).catch(() => {});
      }));
      keep(App.addListener("appUrlOpen", ({ url }) => {
        launchConsumed.current = true; openLink(url);
      }));
    }
    // A cold launch may not emit appUrlOpen. A newer warm link wins if this
    // asynchronous reply arrives late; no navigation after unmount.
    if (!launchConsumed.current && App?.getLaunchUrl) {
      if (!launchRead.current) launchRead.current = Promise.resolve(App.getLaunchUrl());
      launchRead.current.then((result) => {
        if (!live || launchConsumed.current) return;
        launchConsumed.current = true;
        if (result?.url) openLink(result.url);
      }).catch(() => {});
    }

    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
      const link = event.target?.closest?.("a[href]");
      if (!link || !isExternalUrl(link.getAttribute("href"))) return;
      event.preventDefault();
      openExternal(link.href);
    };
    document.addEventListener("click", onClick);

    return () => {
      live = false;
      document.removeEventListener("click", onClick);
      handles.forEach((h) => { try { h.remove?.(); } catch { /* already gone */ } });
    };
  }, [nav]);

  useEffect(() => {
    const Push = nativePlugin("PushNotifications");
    if (!userId || !Push?.register) return undefined;
    let live = true;
    const handles = [];
    const remove = (handle) => {
      try { Promise.resolve(handle?.remove?.()).catch(() => {}); } catch { /* already gone */ }
    };
    const keep = async (pending) => {
      const handle = await pending;
      if (!live) { remove(handle); return false; }
      handles.push(handle);
      return true;
    };
    (async () => {
      // An Android build without its Firebase configuration (a test build;
      // the store's gets it before Gradle) would end the app on register():
      // no notifications there, nothing else changes (PushSetupPlugin.java).
      if (window.Capacitor?.getPlatform?.() === "android" && !(await nativePlugin("PushSetup")?.isReady?.())?.ready) return;
      let status = (await Push.checkPermissions?.())?.receive;
      if (!live) return;
      if (status === "prompt" || status === "prompt-with-rationale") status = (await Push.requestPermissions())?.receive;
      if (!live || status !== "granted") return;
      if (!await keep(Push.addListener("registration", async ({ value }) => {
        if (!live) return;
        try {
          const platform = window.Capacitor?.getPlatform?.() === "ios" ? "ios" : "android";
          const { data } = await api.post("/devices", { token: value, platform, app: APP_TARGET });
          if (live && data?.id) localStorage.setItem("toukky_device_id", data.id);
        } catch { /* registered again at the next launch */ }
      }))) return;
      if (!await keep(Push.addListener("pushNotificationActionPerformed", ({ notification }) => {
        if (!live) return;
        const path = notification?.data?.path;
        if (typeof path === "string" && path.startsWith("/") && !path.startsWith("//")) nav(path);
      }))) return;
      await Push.register();
    })().catch(() => {});
    return () => { live = false; handles.forEach(remove); };
  }, [userId, nav]);

  return null;
}
